import React from 'react';
import { Modal, Button } from 'antd';
import s from './s.module.less';
import { TReview } from 'types/inbox';
// 暂时屏蔽图片生成功能
// import { generateShareImage } from 'utils/generateShareImage';
// 使用 provider home 信息的通用 hook（包含 slugName）
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
// 暂时屏蔽图片上传功能
// import { uploadFile } from 'api/common';

type Props = {
    visible: boolean;
    review: TReview;
    onCancel: () => void;
};

// 固定的分享文案（在“Check out my latest patient review 💬”后换行）
const SHARE_TEXT = 'Check out my latest patient review 💬\nI\'m proud to support my patients and grateful for their feedback. See more reviews and book an appointment with me on Klarity today.';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ShareReviewModal: React.FC<Props> = ({ visible, review, onCancel }) => {
    const [providerSlug, setProviderSlug] = React.useState<string>('');
    // 通过通用的 provider home hook 获取首页信息（包括 slugName）
    const [homeInfo] = useProviderHomeInfo();

    // 弹窗打开时，根据 homeInfo 中的 slugName 设置 providerSlug；如果没有就置为空
    React.useEffect(() => {
        if (!visible) {
            return;
        }
        if (homeInfo?.slugName) {
            setProviderSlug(homeInfo.slugName);
        } else {
            setProviderSlug('');
        }
    }, [visible, homeInfo]);

    // 生成分享链接
    const getShareUrl = () => {
        const slug = providerSlug;
        if (!slug) {
            return 'https://www.helloklarity.com';
        }
        return `https://www.helloklarity.com/provider/${slug}`;
    };

    // 生成分享图片并分享
    const handleShare = async (platform: 'facebook' | 'twitter' | 'linkedin') => {
        try {
            // 暂时屏蔽图片生成和上传代码，使用硬编码的图片URL
            // TODO: 后续可以恢复图片生成功能
            // const imageDataUrl = await generateShareImage({
            //     rating: review.rating || 5,
            //     reviewComment: review.reviewComment || '',
            // });
            // const response = await fetch(imageDataUrl);
            // const blob = await response.blob();
            // const imageFile = new File([blob], 'review-share.png', { type: 'image/png' });
            // const formData = new FormData();
            // formData.append('file', imageFile);
            // const uploadResult = await uploadFile(formData);
            // let imageUrl = '';
            // if (!uploadResult.error && uploadResult.data?.data) {
            //     imageUrl = uploadResult.data.data;
            // }

            // 使用硬编码的图片URL
            // 注意：图片需要通过目标页面的 Open Graph 标签显示，这里暂时不使用
            // const imageUrl = 'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/30ff0bd9-e36c-4b37-ba8e-e89ba8609d0c.png';

            const shareUrl = getShareUrl();

            // 优先使用Web Share API（如果支持），可以直接分享图片文件
            // 暂时屏蔽，因为不再生成图片文件
            // if (navigator.share && navigator.canShare && imageFile) {
            //     try {
            //         const shareData: ShareData = {
            //             title: 'Check out my latest patient review',
            //             text: SHARE_TEXT.replace(/\n/g, ' '),
            //             url: shareUrl,
            //         };
            //         if (navigator.canShare({ files: [imageFile] })) {
            //             shareData.files = [imageFile];
            //             await navigator.share(shareData);
            //             message.success('Share dialog opened');
            //             return;
            //         } else {
            //             await navigator.share(shareData);
            //             message.success('Share dialog opened');
            //             return;
            //         }
            //     } catch (shareError) {
            //         console.log('Web Share API not available or cancelled:', shareError);
            //     }
            // }

            // 如果不支持Web Share API，使用传统方法
            // 注意：传统方法无法直接传递图片，图片需要通过目标页面的Open Graph标签显示
            if (platform === 'facebook') {
                // Facebook 分享方式：
                // 1. 使用 Feed 对话框（需要 App ID，可以自定义标题、描述、图片）
                // 2. 使用 sharer.php（简单但无法自定义内容，依赖 Open Graph 标签）
                // 注意：Facebook 不允许预填用户的消息文本框，用户需要手动输入

                // 使用 Feed 对话框实现自定义分享（需要 Facebook App ID）
                const FACEBOOK_APP_ID = '331213246266862'; // 项目中的 Facebook App ID
                const shareText = SHARE_TEXT.replace(/\n/g, ' '); // 移除换行符
                const imageUrl = 'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/30ff0bd9-e36c-4b37-ba8e-e89ba8609d0c.png';

                // Facebook Feed 对话框参数：
                // - app_id: Facebook 应用 ID（必需）
                // - link: 要分享的链接
                // - picture: 分享图片的 URL
                // - name: 分享标题
                // - description: 分享描述（文案）
                // - redirect_uri: 分享后的重定向 URL
                const facebookFeedUrl = 'https://www.facebook.com/dialog/feed?'
                    + `app_id=${FACEBOOK_APP_ID}&`
                    + `link=${encodeURIComponent(shareUrl)}&`
                    + `picture=${encodeURIComponent(imageUrl)}&`
                    + `name=${encodeURIComponent('Check out my latest patient review\n')}&`
                    + `description=${encodeURIComponent(shareText)}&`
                    + `redirect_uri=${encodeURIComponent(shareUrl)}&`
                    + 'display=popup';

                window.open(facebookFeedUrl, '_blank', 'width=600,height=400');
            } else if (platform === 'twitter') {
                // Twitter 支持 text 参数
                // 注意：Twitter 会自动缩短 URL，所以我们可以将完整文案放在 text 中
                // Twitter 的字符限制是 280 个字符（包括 URL），但 URL 会被自动缩短为约 23 个字符
                // 所以实际可用文本长度约为 280 - 23 = 257 个字符
                const shareText = SHARE_TEXT.replace(/\n/g, ' '); // 移除换行符
                // Twitter 会自动处理超长文本，但为了更好的体验，我们手动截断
                const maxTextLength = 250; // 留一些缓冲空间
                const truncatedText = shareText.length > maxTextLength
                    ? `${shareText.substring(0, maxTextLength - 3)}...`
                    : shareText;

                // Twitter Intent API: text 参数必须在 url 参数之前
                // 格式：https://twitter.com/intent/tweet?text=文本&url=链接
                const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(truncatedText)}&url=${encodeURIComponent(shareUrl)}`;
                window.open(twitterUrl, '_blank', 'width=600,height=400');
            } else if (platform === 'linkedin') {
                // LinkedIn 分享方式：
                // 1. 使用 shareArticle API（可以自定义标题、摘要，但图片需要通过 Open Graph 标签）
                // 2. 使用 share-offsite API（简单但功能有限）
                // 注意：LinkedIn 不支持通过 URL 参数传递图片，图片需要通过目标页面的 Open Graph 标签显示

                const shareText = SHARE_TEXT.replace(/\n/g, ' '); // 移除换行符
                // 注意：LinkedIn 不支持通过 URL 参数传递图片
                // 图片需要通过目标页面的 Open Graph 标签显示
                // const imageUrl = 'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/30ff0bd9-e36c-4b37-ba8e-e89ba8609d0c.png';

                // LinkedIn shareArticle API 参数：
                // - mini: 使用简化版分享对话框（推荐）
                // - url: 要分享的链接（必需）
                // - title: 分享标题（最多 200 个字符）
                // - summary: 分享摘要/描述（最多 256 个字符）
                // 注意：图片需要通过目标页面的 Open Graph 标签显示，不能通过 URL 参数传递
                const shareTitle = 'Check out my latest patient review';
                // LinkedIn summary 限制为 256 个字符
                const maxSummaryLength = 256;
                const truncatedSummary = shareText.length > maxSummaryLength
                    ? `${shareText.substring(0, maxSummaryLength - 3)}...`
                    : shareText;

                // 使用 shareArticle API（支持自定义标题和摘要）
                const linkedInUrl = 'https://www.linkedin.com/shareArticle?'
                    + 'mini=true&'
                    + `url=${encodeURIComponent(shareUrl)}&`
                    + `title=${encodeURIComponent(shareTitle)}&`
                    + `summary=${encodeURIComponent(truncatedSummary)}`;

                window.open(linkedInUrl, '_blank', 'width=600,height=400');
            }

            // 图片URL已硬编码，不需要检查上传失败
            // if (!imageUrl) {
            //     message.warning('Image upload failed, sharing link only. The image may not appear in the share preview.');
            // }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to share:', error);
            // message.error('Failed to share. Please try again.');
        }
    };

    // Facebook 分享
    const handleFacebookShare = () => {
        handleShare('facebook');
    };

    // Twitter/X 分享
    const handleTwitterShare = () => {
        handleShare('twitter');
    };

    // LinkedIn 分享
    const handleLinkedInShare = () => {
        handleShare('linkedin');
    };

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            footer={null}
            title="Share the review to social media"
            width={650}
            className={s.shareModalWrapper}
            destroyOnClose
            maskClosable
        >
            <div className={s.shareModal}>
                <div className={s.shareContent}>
                    <p className={s.shareDescription}>
                        Highlight your patient care and build trust by sharing
                        the review on your social platforms. Select a platform
                        to share:
                    </p>
                    <div className={s.socialIcons}>
                        <Button
                            className={s.socialIconButton}
                            onClick={handleFacebookShare}
                            aria-label="Share on Facebook"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="55"
                                height="55"
                                viewBox="0 0 55 55"
                                fill="none"
                            >
                                <path
                                    d="M54.9838 27.501C54.9838 41.3902 44.6913 52.8727 31.3205 54.736C30.0703 54.9094 28.7912 55 27.4929 55C25.9942 55 24.5224 54.8805 23.0892 54.6493C9.99776 52.5413 0 41.1879 0 27.501C0 12.313 12.3094 0 27.4909 0C42.6725 0 54.9838 12.313 54.9838 27.501Z"
                                    fill="#1877F2"
                                />
                                <path
                                    d="M31.3219 22.0807V28.0715H38.7306L37.5575 36.1414H31.3219V54.7343C30.0717 54.9077 28.7926 54.9982 27.4942 54.9982C25.9955 54.9982 24.5238 54.8788 23.0906 54.6475V36.1414H16.2578V28.0715H23.0906V20.7415C23.0906 16.194 26.7757 12.5059 31.3238 12.5059V12.5097C31.3373 12.5097 31.3488 12.5059 31.3623 12.5059H38.7325V19.4852H33.9167C32.4854 19.4852 31.3238 20.6471 31.3238 22.0788L31.3219 22.0807Z"
                                    fill="white"
                                />
                            </svg>
                        </Button>
                        <Button
                            className={s.socialIconButton}
                            onClick={handleTwitterShare}
                            aria-label="Share on Twitter/X"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="55"
                                height="55"
                                viewBox="0 0 55 55"
                                fill="none"
                            >
                                <path
                                    d="M54.9819 27.501C54.9819 41.3902 44.6894 52.8727 31.3186 54.736C30.0684 54.9094 28.7893 55 27.4909 55C25.9922 55 24.5205 54.8805 23.0873 54.6493C9.99775 52.5413 0 41.1879 0 27.501C0 12.313 12.3094 0 27.4929 0C42.6763 0 54.9857 12.313 54.9857 27.501H54.9819Z"
                                    fill="#1C1C1B"
                                />
                                <path
                                    d="M11.1493 12.1289L23.8285 29.0858L11.0703 42.8729H13.9425L25.1134 30.8027L34.1384 42.8729H43.9107L30.5187 24.9622L42.3947 12.1289H39.5225L29.2358 23.2453L20.9236 12.1289H11.1512H11.1493ZM15.3718 14.2447H19.8602L39.6843 40.7571H35.1959L15.3718 14.2447Z"
                                    fill="white"
                                />
                            </svg>
                        </Button>
                        <Button
                            className={s.socialIconButton}
                            onClick={handleLinkedInShare}
                            aria-label="Share on LinkedIn"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="55"
                                height="55"
                                viewBox="0 0 55 55"
                                fill="none"
                            >
                                <path
                                    d="M27.5545 0H27.4293C12.2805 0 0 12.2841 0 27.4374V27.5626C0 42.7159 12.2805 55 27.4293 55H27.5545C42.7033 55 54.9838 42.7159 54.9838 27.5626V27.4374C54.9838 12.2841 42.7033 0 27.5545 0Z"
                                    fill="#007EBB"
                                />
                                <path
                                    d="M13.1119 18.2831C12.3875 17.6106 12.0273 16.7782 12.0273 15.7877C12.0273 14.7973 12.3895 13.9283 13.1119 13.2538C13.8362 12.5813 14.7685 12.2441 15.9108 12.2441C17.0532 12.2441 17.9489 12.5813 18.6713 13.2538C19.3956 13.9263 19.7558 14.7723 19.7558 15.7877C19.7558 16.8032 19.3937 17.6106 18.6713 18.2831C17.947 18.9556 17.0281 19.2928 15.9108 19.2928C14.7936 19.2928 13.8362 18.9556 13.1119 18.2831ZM19.1471 22.1408V42.7588H12.6341V22.1408H19.1471Z"
                                    fill="#FEFFFC"
                                />
                                <path
                                    d="M40.827 24.1775C42.2467 25.7191 42.9556 27.8348 42.9556 30.5287V42.3946H36.7701V31.3649C36.7701 30.0065 36.4176 28.9505 35.7145 28.199C35.0114 27.4475 34.0636 27.0698 32.877 27.0698C31.6904 27.0698 30.7425 27.4456 30.0394 28.199C29.3363 28.9505 28.9838 30.0065 28.9838 31.3649V42.3946H22.7617V22.083H28.9838V24.7768C29.6137 23.8789 30.4633 23.1698 31.5305 22.6476C32.5977 22.1254 33.7978 21.8652 35.1327 21.8652C37.5098 21.8652 39.4092 22.636 40.827 24.1775Z"
                                    fill="#FEFFFC"
                                />
                            </svg>
                        </Button>
                    </div>
                </div>
                <div className={s.shareFooter}>
                    <div className={s.shareFooterBorder} />
                </div>
            </div>
        </Modal>
    );
};

export default ShareReviewModal;
